'use client'

import * as React from 'react'
import { createContainer } from 'unstated-next'

import {
  NextDialog,
  NextDialogBody,
  NextDialogHeader,
  NextDialogProvider,
  NextDialogTitle,
} from '../../../../../app/_components/dialogs/NextDialog.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { joinVariantWaitlist } from '../../../../../app/_utils/joinVariantWaitlist'
import { Alert } from '../../../../../brunswick/components/Alert'

import { Divider, Instructions, ProductTitle } from './styles'
import { WaitlistForm } from './WaitlistForm'

const WaitlistModal = () => {
  const {
    waitlist: waitlistCopy,
    magnolia: { general },
  } = useTranslation()

  const {
    closeWaitlistModal,
    errorMessage,
    modalTitle,
    shouldDisplayWaitlistModal,
    successMessage,
  } = useWaitlistModal()

  return (
    <NextDialogProvider>
      <NextDialog onClose={closeWaitlistModal} open={shouldDisplayWaitlistModal}>
        <NextDialogHeader closeText={general.close}>
          <NextDialogTitle>{waitlistCopy.modal.headline}</NextDialogTitle>
        </NextDialogHeader>
        <NextDialogBody style={{ textAlign: 'left' }}>
          <Instructions>{waitlistCopy.modal.instructions}</Instructions>
          <Divider />
          <ProductTitle textType='h2'>{modalTitle}</ProductTitle>
          {!successMessage && <WaitlistForm />}
          {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
          {successMessage && <Alert severity='success'>{successMessage}</Alert>}
        </NextDialogBody>
      </NextDialog>
    </NextDialogProvider>
  )
}

export interface UseWaitlistModalProps {
  colorDisplayValue: string
  productTitle: string
  variantShopifyId: string | undefined | null
}

function useWaitlistModalImpl(props: UseWaitlistModalProps | undefined) {
  const { productTitle, variantShopifyId, colorDisplayValue } = props!
  const [shouldDisplayWaitlistModal, setShouldDisplayWaitlistModal] = React.useState<boolean>(false)
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null)

  function openWaitlistModal(): void {
    setShouldDisplayWaitlistModal(true)
  }

  function clearMessages(): void {
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  function closeWaitlistModal(): void {
    clearMessages()
    setShouldDisplayWaitlistModal(false)
  }

  return {
    clearMessages,
    closeWaitlistModal,
    errorMessage,
    openWaitlistModal,
    modalTitle: `${colorDisplayValue} ${productTitle}`,
    variantShopifyId,
    sendWaitlistRequest: joinVariantWaitlist,
    setErrorMessage,
    setSuccessMessage,
    shouldDisplayWaitlistModal,
    successMessage,
  }
}

const WaitlistModalContainer = createContainer(useWaitlistModalImpl)
export const useWaitlistModal = WaitlistModalContainer.useContainer
export const WaitlistModalProvider = ({
  children,
  ...rest
}: UseWaitlistModalProps & { children: React.ReactNode }) => {
  return (
    <WaitlistModalContainer.Provider initialState={rest}>
      <WaitlistModal />
      {children}
    </WaitlistModalContainer.Provider>
  )
}
